import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { Partner } from 'src/modules/designStudio/providers';
import type { DesignStudioColor } from 'src/shared/graphql/generated';
import type { GenericProductSettings } from 'src/shared/graphql/requests';

import type {
  FilterTracker,
  FilteredColorsData,
  FilteredTexturesData,
  Textures,
  Thumbnail,
} from './types';

const addThumbnailsToMenuItems = ({
  menuItems,
  textures,
}: {
  menuItems: MenuItemType[];
  textures: Textures;
}) => {
  // add thumbnail urls from products endpoint
  const hydratedMenuItems = menuItems.map(item => {
    const thumbnail: Thumbnail = item.hasThumbnail
      ? textures?.find(texture => texture.id === item.textureId)?.texture
          ?.thumbnail.url
      : undefined;

    return {
      ...item,
      thumbnail,
    };
  });

  return { menuItems: hydratedMenuItems };
};

const filterMenuItems = (
  menuItems: MenuItemType[],
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const updatedFilterTracker = filterTracker;
  if (settings) {
    const filteredMenuItems = menuItems.filter(menuItem => {
      if (settings[menuItem.category]?.disabled_types) {
        updatedFilterTracker[menuItem.category as DesignConfigCategories] =
          true;
      }
      return !settings[menuItem.category]?.disabled_types?.includes(
        menuItem.type,
      );
    });

    return {
      menuItems: filteredMenuItems,
      filterTracker: updatedFilterTracker,
    };
  }

  return { menuItems, filterTracker };
};

const filterTextures = (
  menuItems: MenuItemType[],
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const filteredTextures: FilteredTexturesData = {};
  const updatedFilterTracker = filterTracker;

  const isMenuItemFilteredById = (
    menuItem: MenuItemType,
    disabledTextureIds: { [key: string]: boolean | undefined },
  ) =>
    !!disabledTextureIds[
      `${menuItem.type.toUpperCase()}::${menuItem.textureId}`
    ];

  const isMenuItemFilteredByColorGroup = (
    menuItem: MenuItemType,
    disabledTextureColorGroups: { [key: string]: boolean | undefined },
  ) =>
    !!disabledTextureColorGroups[
      `${menuItem.type.toUpperCase()}::${
        menuItem.colorGroup?.toUpperCase() ?? ''
      }`
    ];

  if (settings) {
    Object.keys(settings).forEach(category => {
      const filteredCategoryTextures =
        filteredTextures[category as DesignConfigCategories] ?? {};
      const categorySettings = settings[category] ?? {};

      const disabledTextureIds: { [key: string]: boolean } = {};
      (categorySettings.disabled_textures ?? []).forEach(disabledTexture => {
        disabledTextureIds[disabledTexture.toUpperCase()] = true;
      });

      const disabledTextureColorGroups: { [key: string]: boolean } = {};
      (categorySettings.disabled_texture_color_groups ?? []).forEach(
        disabledColorGroup => {
          disabledTextureColorGroups[disabledColorGroup.toUpperCase()] = true;
        },
      );

      menuItems.forEach(menuItem => {
        const isDisabled =
          isMenuItemFilteredById(menuItem, disabledTextureIds) ||
          isMenuItemFilteredByColorGroup(menuItem, disabledTextureColorGroups);

        if (isDisabled) {
          const filteredTypeTextures =
            filteredCategoryTextures[menuItem.type] || {};
          filteredTypeTextures[menuItem.textureId] = true;
          filteredCategoryTextures[menuItem.type] = filteredTypeTextures;
        }
      });

      if (Object.keys(filteredCategoryTextures).length > 0) {
        filteredTextures[category as DesignConfigCategories] =
          filteredCategoryTextures;
      }
    });
  }

  return { filteredTextures, filterTracker: updatedFilterTracker };
};

const filterColors = (
  partner: Partner,
  filterTracker: FilterTracker,
  colors: DesignStudioColor[],
  windowColors: DesignStudioColor[],
  settings?: GenericProductSettings,
) => {
  const filteredColors: FilteredColorsData = {
    [DesignConfigCategories.ROOF]: colors,
    [DesignConfigCategories.WALL]: colors,
    [DesignConfigCategories.TRIM]: colors,
    [DesignConfigCategories.WINDOW]: windowColors,
  };
  const updatedFilterTracker = filterTracker;

  if (settings) {
    Object.keys(settings).forEach(category => {
      const colorDataSet: DesignStudioColor[] =
        category === DesignConfigCategories.WINDOW ? windowColors : colors;

      const filtered = colorDataSet.filter(color => {
        if (settings[category]?.disabled_colors) {
          updatedFilterTracker[category as DesignConfigCategories] = true;
        }
        return !settings[category]?.disabled_colors?.includes(color.color);
      });

      filteredColors[category as DesignConfigCategories] = filtered;
    });
  } else if (partner === Partner.JAMES_HARDIE) {
    filteredColors[DesignConfigCategories.TRIM] = filteredColors[
      DesignConfigCategories.TRIM
    ].filter(color => !!color.trim);
  }

  return { filteredColors, filterTracker: updatedFilterTracker };
};

export {
  addThumbnailsToMenuItems,
  filterMenuItems,
  filterTextures,
  filterColors,
};
